import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, EmptyState } from "@/components/common";
import { AdminLayout, btn, btnGhost, inp, lbl } from "@/pages/admin/adminShared";
import { FileUpload, fileUrl } from "@/components/FileUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, GripVertical, Users } from "lucide-react";

const EMPTY = { name: "", title: "", bio: "", favourite_dance: "", years_teaching: "", facebook_url: "", photo_path: "", active: true };

export default function ManageInstructors() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const dragId = useRef(null);

  const { data: rows, isLoading, refetch } = useQuery({ queryKey: ["admin-instructors"], queryFn: async () => (await api.get("/admin/instructors")).data });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const openNew = () => { setF(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (it) => { setF({ ...EMPTY, ...it }); setEditId(it.id); setOpen(true); };
  const save = async () => {
    if (!f.name) { toast.error("Please enter a name"); return; }
    const payload = { name: f.name, title: f.title, bio: f.bio, favourite_dance: f.favourite_dance, years_teaching: f.years_teaching, facebook_url: f.facebook_url, photo_path: f.photo_path, active: f.active };
    try {
      if (editId) await api.put(`/admin/instructors/${editId}`, payload);
      else await api.post("/admin/instructors", payload);
      toast.success(editId ? "Instructor updated" : "Instructor added");
      setOpen(false); refetch();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async (it) => { if (!window.confirm(`Delete ${it.name}?`)) return; await api.delete(`/admin/instructors/${it.id}`); refetch(); toast("Instructor deleted"); };

  const onDrop = async (targetId) => {
    const ids = rows.map((r) => r.id);
    const from = ids.indexOf(dragId.current);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0 || from === to) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    qc.setQueryData(["admin-instructors"], ids.map((id) => rows.find((r) => r.id === id)));
    await api.post("/admin/instructors/reorder", { ids });
    refetch();
  };

  return (
    <AdminLayout role={user?.role} title="Instructors" subtitle="Showcase your team. Drag to reorder how they appear on the public page."
      actions={<button className={btn} onClick={openNew} data-testid="add-instructor-button"><Plus size={16} /> Add instructor</button>}>
      {isLoading ? <Spinner /> : !rows?.length ? (
        <EmptyState title="No instructors yet" subtitle="Add your team so dancers can meet them." icon={Users} />
      ) : (
        <div className="space-y-2">
          {rows.map((it) => (
            <div key={it.id} draggable onDragStart={() => (dragId.current = it.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(it.id)}
              data-testid={`instructor-row-${it.id}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3">
              <GripVertical size={18} className="cursor-grab text-[#C9B8AC]" />
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-[#EBE1D8]">
                {it.photo_path ? <img src={fileUrl(it.photo_path)} alt={it.name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-[#C9B8AC]"><Users size={20} /></div>}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[#2D1E18]">{it.name}{!it.active && <span className="ml-2 text-xs text-[#6E5B51]">(hidden)</span>}</p>
                <p className="text-sm text-[#6E5B51]">{it.title}</p>
              </div>
              <button onClick={() => openEdit(it)} className="rounded-lg p-1.5 text-[#5C4A42] hover:bg-secondary" aria-label="Edit"><Pencil size={15} /></button>
              <button onClick={() => del(it)} className="rounded-lg p-1.5 text-[#6E5B51] hover:text-[#991B1B]" aria-label="Delete"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit instructor" : "Add instructor"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Add or edit an instructor's photo, name, role and bio.</DialogDescription>
          <div className="space-y-3">
            <FileUpload label="Photo" value={f.photo_path} onChange={(path) => setF({ ...f, photo_path: path })} accept=".png,.jpg,.jpeg,.webp" kind="image" testid="instructor-photo-upload" />
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Name</label><input className={inp} value={f.name} onChange={set("name")} data-testid="instructor-name" /></div>
              <div><label className={lbl}>Title / role</label><input className={inp} value={f.title} onChange={set("title")} placeholder="e.g. Lead Instructor" /></div>
            </div>
            <div><label className={lbl}>Bio</label><textarea className={inp} rows={4} value={f.bio} onChange={set("bio")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Favourite dance</label><input className={inp} value={f.favourite_dance} onChange={set("favourite_dance")} /></div>
              <div><label className={lbl}>Years teaching</label><input className={inp} value={f.years_teaching} onChange={set("years_teaching")} placeholder="e.g. 8 years" /></div>
            </div>
            <div><label className={lbl}>Facebook URL</label><input className={inp} value={f.facebook_url} onChange={set("facebook_url")} placeholder="https://facebook.com/..." /></div>
            <label className="flex items-center gap-2 text-sm text-[#5C4A42]"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="h-4 w-4 accent-[#C85A32]" /> Show on public page</label>
          </div>
          <DialogFooter>
            <button className={btnGhost} onClick={() => setOpen(false)}>Cancel</button>
            <button className={btn} onClick={save} data-testid="instructor-save">{editId ? "Save changes" : "Add instructor"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
